import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const FORM_URL = 'https://readdy.ai/api/form/d669i2s2hrrhr32vesl0';

export default function QuoteForm() {
  const { t } = useTranslation();

  const serviceOptions = [
    'Réparation Fast-Track en aéroport',
    'Gestion complète des sinistres bagages',
    'Contrat de maintenance annuel',
    'Logistique & livraison premium',
    'Atelier de réparation dédié',
    'Solution sur mesure',
  ];

  const volumeOptions = [
    'Moins de 100 bagages / mois',
    '100 à 500 bagages / mois',
    '500 à 2 000 bagages / mois',
    'Plus de 2 000 bagages / mois',
  ];

  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    poste: '',
    service: '',
    volume: '',
    message: '',
    rgpd: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    if (name === 'message' && value.length > 500) return;
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (name === 'message') setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.rgpd) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('Prénom', formData.prenom);
      body.append('Nom', formData.nom);
      body.append('email', formData.email);
      body.append('Téléphone', formData.telephone);
      body.append('Entreprise', formData.entreprise);
      body.append('Poste', formData.poste);
      body.append('Service demandé', formData.service);
      body.append('Volume estimé', formData.volume);
      body.append('Message', formData.message);

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({
          prenom: '',
          nom: '',
          email: '',
          telephone: '',
          entreprise: '',
          poste: '',
          service: '',
          volume: '',
          message: '',
          rgpd: false,
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FF6B6B] focus:ring-[#FF6B6B] focus:ring-opacity-10 focus:ring-2 transition-all';

  return (
    <section className="pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Left — Avantages */}
          <div className="lg:col-span-2">
            <h2
              className="text-3xl font-bold text-gray-900 mb-4 leading-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t('contactPage.formSideTitle')}
              <br />
              <span className="bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] bg-clip-text text-transparent">
                {t('contactPage.formSideTitleHighlight')}
              </span>
            </h2>
            <p className="text-gray-500 text-sm mb-10 leading-relaxed">
              {t('contactPage.formSideDesc')}
            </p>

            <div className="space-y-6 mb-12">
              {[
                {
                  icon: 'ri-timer-flash-line',
                  title: t('contactPage.advantage1Title'),
                  desc: t('contactPage.advantage1Desc'),
                },
                {
                  icon: 'ri-money-euro-circle-line',
                  title: t('contactPage.advantage2Title'),
                  desc: t('contactPage.advantage2Desc'),
                },
                {
                  icon: 'ri-shield-check-line',
                  title: t('contactPage.advantage3Title'),
                  desc: t('contactPage.advantage3Desc'),
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: t('contactPage.advantage4Title'),
                  desc: t('contactPage.advantage4Desc'),
                },
                {
                  icon: 'ri-bar-chart-grouped-line',
                  title: t('contactPage.advantage5Title'),
                  desc: t('contactPage.advantage5Desc'),
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-11 h-11 bg-gradient-to-br from-[#FF6B6B]/10 to-[#FF8E53]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-[#FF6B6B] text-lg`}></i>
                  </div>
                  <div>
                    <h4
                      className="text-sm font-semibold text-gray-900 mb-1"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {item.title}
                    </h4>
                    <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Urgence Card */}
            <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-xl flex items-center justify-center">
                  <i className="ri-phone-fill text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider font-medium">
                    {t('contactPage.urgencyLabel')}
                  </p>
                  <div className="text-white text-sm font-bold space-y-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <p>🇧🇪 +32 10 39 83 83</p>
                    <p>🇨🇭 +41 22 562 42 48</p>
                    <p>🇲🇦 +212 5 31 18 84 66</p>
                    <p>🇺🇸 +1 832 295 0203</p>
                    <p>🇦🇪 +971 58 514 6914</p>
                  </div>
                </div>
              </div>
              <p className="text-white/40 text-xs leading-relaxed">
                {t('contactPage.urgencyDesc')}
              </p>
            </div>
          </div>

          {/* Right — Formulaire */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-8 md:p-10">
              <h3
                className="text-xl font-bold text-gray-900 mb-1"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {t('contactPage.formTitle')}
              </h3>
              <p className="text-sm text-gray-400 mb-8">
                {t('contactPage.formSubtitle')}
              </p>

              {submitStatus === 'success' ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#4ECDC4] to-[#44A08D] rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-white text-4xl"></i>
                  </div>
                  <h4
                    className="text-2xl font-bold text-gray-900 mb-3"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {t('contactPage.successTitle')}
                  </h4>
                  <p className="text-gray-500 max-w-sm mx-auto">
                    {t('contactPage.successDesc')}
                  </p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="mt-8 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('contactPage.successButton')}
                  </button>
                </div>
              ) : (
                <form
                  id="quote-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {/* Prénom / Nom */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                        {t('contactPage.labelFirstName')}
                      </label>
                      <input
                        type="text"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        placeholder={t('contactPage.placeholderFirstName')}
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                        {t('contactPage.labelLastName')}
                      </label>
                      <input
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        placeholder={t('contactPage.placeholderLastName')}
                        className={inputClass}
                        required
                      />
                    </div>
                  </div>

                  {/* Email / Téléphone */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                        {t('contactPage.labelEmail')}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contactPage.placeholderEmail')}
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                        {t('contactPage.labelPhone')}
                      </label>
                      <input
                        type="tel"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder={t('contactPage.placeholderPhone')}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Entreprise / Poste */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                        {t('contactPage.labelCompany')}
                      </label>
                      <input
                        type="text"
                        name="entreprise"
                        value={formData.entreprise}
                        onChange={handleChange}
                        placeholder={t('contactPage.placeholderCompany')}
                        className={inputClass}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                        {t('contactPage.labelPosition')}
                      </label>
                      <input
                        type="text"
                        name="poste"
                        value={formData.poste}
                        onChange={handleChange}
                        placeholder={t('contactPage.placeholderPosition')}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Service / Volume */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                        {t('contactPage.labelService')}
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className={`${inputClass} cursor-pointer`}
                        required
                      >
                        <option value="">{t('contactPage.selectService')}</option>
                        {serviceOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                        {t('contactPage.labelVolume')}
                      </label>
                      <select
                        name="volume"
                        value={formData.volume}
                        onChange={handleChange}
                        className={`${inputClass} cursor-pointer`}
                      >
                        <option value="">{t('contactPage.selectVolume')}</option>
                        {volumeOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wider">
                      {t('contactPage.labelMessage')}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contactPage.placeholderMessage')}
                      rows={5}
                      maxLength={500}
                      className={`${inputClass} resize-none`}
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1 text-right">
                      {charCount}/500 {t('contactPage.charCount')}
                    </p>
                  </div>

                  {/* RGPD */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="rgpd"
                      checked={formData.rgpd}
                      onChange={handleChange}
                      className="mt-1 w-4 h-4 accent-[#FF6B6B] cursor-pointer"
                      required
                    />
                    <label
                      className="text-xs text-gray-500 leading-relaxed cursor-pointer"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, rgpd: !prev.rgpd }))
                      }
                    >
                      {t('contactPage.rgpdText')}{' '}
                      <a
                        href="#"
                        className="text-[#FF6B6B] underline"
                        rel="nofollow"
                      >
                        {t('contactPage.rgpdLink')}
                      </a>
                      .
                    </label>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.rgpd}
                    className="w-full py-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white rounded-xl font-semibold text-sm hover:shadow-xl hover:shadow-[#FF6B6B]/20 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        {t('contactPage.submitting')}
                      </>
                    ) : (
                      <>
                        {t('contactPage.submitButton')}
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>

                  {submitStatus === 'error' && (
                    <p className="text-red-500 text-sm text-center flex items-center justify-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      {t('contactPage.errorMessage')}
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
